import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import SettingsPanel from '../components/SettingsPanel';
import ShareButton from '../components/ShareButton';
import './Dashboard.css';
import orangeLogo from '../assets/orangelog.png';

const T = {
  accent:  '#dd3333',
  header:  '#111111',
  border:  '#e8e8e8',
  bodyText:'#444',
  headText:'#111',
  meta:    '#777',
  lightBg: '#f4f4f4',
  white:   '#fff',
};

const GENRE_COLOR = {
  'National News': '#dd3333',
  'State News':    '#4db2ec',
  'Local News':    '#27ae60',
  'Sports':        '#e67e22',
  'Technology':    '#8e44ad',
};

const GENRE_ROUTES = [
  { label: 'National News', to: '/national-news', color: '#dd3333' },
  { label: 'State News',    to: '/state-news',    color: '#4db2ec' },
  { label: 'Sports',        to: '/sports',        color: '#e67e22' },
  { label: 'Technology',    to: '/technology',    color: '#8e44ad' },
  { label: 'Local News',    to: '/local-news',    color: '#27ae60' },
];

function SideCard({ article, onClick }) {
  const color = GENRE_COLOR[article.genre_tree?.main] || T.accent;
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'flex', gap: '8px', cursor: 'pointer', paddingBottom: '10px', borderBottom: `1px solid ${T.border}`, marginBottom: '10px' }}>
      {article.headline_image && (
        <div style={{ width: '60px', height: '45px', flexShrink: 0, overflow: 'hidden' }}>
          <img src={article.headline_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '10px', fontWeight: '800', color, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px', fontFamily: 'Arial, sans-serif' }}>
          {article.genre_tree?.main}
        </div>
        <div style={{ fontSize: '13px', fontWeight: '700', color: hov ? color : T.headText, lineHeight: '18px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', transition: 'color 0.12s', fontFamily: 'Georgia, serif' }}>
          {article.title}
        </div>
        <div style={{ fontSize: '11px', color: T.meta, marginTop: '2px', fontFamily: 'Arial, sans-serif' }}>
          {article.company_name}
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [user, setUser] = useState(null);
  const [readArticles, setReadArticles] = useState([]);
  const [companyMap, setCompanyMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [forYouArticles, setForYouArticles]       = useState([]);
  const [forYouHasSignal, setForYouHasSignal]     = useState(null);
  const [userInterests, setUserInterests]         = useState([]);
  const [interestsExpanded, setInterestsExpanded] = useState(false);
  const [sortBy, setSortBy] = useState('date_purchased_desc');
  const [readFilter, setReadFilter] = useState('all');
  const [refundConfirm, setRefundConfirm]       = useState(null);
  const [refundMsg, setRefundMsg]               = useState('');
  const [returnsEnabled, setReturnsEnabled]     = useState(true);
  const [returnsThisMonth, setReturnsThisMonth] = useState(0);
  const [following, setFollowing] = useState([]);
  const [followingAuthors, setFollowingAuthors] = useState([]);
  const [followingNames, setFollowingNames] = useState({ companies: [], authors: [] });
  const [selectedFollow, setSelectedFollow] = useState('all');
  const [gearMenuOpen, setGearMenuOpen] = useState(null);
  const [followListOpen, setFollowListOpen] = useState(false);
  const [articleGearOpen, setArticleGearOpen] = useState(null);
  const [archiveConfirm, setArchiveConfirm] = useState(null);
  const [removeConfirm, setRemoveConfirm]   = useState(null);
  const [hiddenFromFeedIds, setHiddenFromFeedIds] = useState(new Set());
  const [brokenLinkReported, setBrokenLinkReported] = useState(new Set()); // set of articleIds already reported this session
  const [archiveSectionOpen, setArchiveSectionOpen] = useState(false);
  const [selectedArchiveGenre, setSelectedArchiveGenre] = useState(null);
  const [archivedIds, setArchivedIds] = useState(new Set());
  const [genreFilter, setGenreFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState(null);
  const [tokenDropOpen, setTokenDropOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [mobileSection, setMobileSection] = useState(null); // 'following'|'archive'|'settings'
  const tokenDropRef = useRef(null);

  const { accessToken: token, clearSession } = useAuth();
  const userType = localStorage.getItem('userType');

  useEffect(() => { fetchUserAndArticles(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!tokenDropOpen) return;
    const handler = (e) => {
      if (tokenDropRef.current && !tokenDropRef.current.contains(e.target)) setTokenDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [tokenDropOpen]);

  useEffect(() => {
    if (!gearMenuOpen) return;
    const close = () => setGearMenuOpen(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [gearMenuOpen]);

  useEffect(() => {
    if (!articleGearOpen) return;
    const close = () => { setArticleGearOpen(null); setArchiveConfirm(null); setRemoveConfirm(null); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [articleGearOpen]);

  // Guard: only user accounts can access the hub (must be after all hooks)
  if (!token || userType !== 'user') {
    return <Navigate to="/login" replace />;
  }

  async function fetchUserAndArticles() {
    try {
      setLoading(true);
      const authHeader = { Authorization: `Bearer ${token}` };
      const userResponse = await axios.get('/api/users/me', { headers: authHeader });
      const user = userResponse.data;
      setUser(user);
      if (user.username !== slug) {
        navigate(`/${user.username}`, { replace: true });
        return;
      }
      setReadArticles(user.purchased?.filter(item => item.read).map(item => item.articleId?.toString?.()) || []);
      setArchivedIds(new Set(user.purchased?.filter(item => item.archived && !item.returned).map(item => item.articleId?.toString?.()) || []));
      setHiddenFromFeedIds(new Set(user.purchased?.filter(item => item.hiddenFromFeed && !item.returned).map(item => item.articleId?.toString?.()) || []));
      setUserInterests(user.interests || []);
      setReturnsEnabled(user.returnsEnabled !== false);
      setReturnsThisMonth(user.returnsThisMonth || 0);
      setFollowing((user.following || []).map(String));
      setFollowingAuthors((user.followingAuthors || []).map(String));
      try {
        const followRes = await axios.get('/api/users/me/following', { headers: authHeader });
        setFollowingNames(followRes.data);
      } catch (_) {}
      const articlesResponse = await axios.get('/api/articles');
      setArticles(articlesResponse.data);
      const companiesResponse = await axios.get('/api/companies', { headers: authHeader });
      const compMap = {};
      companiesResponse.data.forEach(comp => {
        compMap[comp._id] = { name: comp.company_name, heroImage: comp.hero_image || null, cardStyle: comp.cardStyle || '6' };
      });
      setCompanyMap(compMap);
      try {
        const forYouResp = await axios.get('/api/articles/for-you?limit=5', { headers: authHeader });
        setForYouArticles(forYouResp.data.articles || []);
        setForYouHasSignal(forYouResp.data.hasSignal);
      } catch (_) { setForYouArticles([]); }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load hub. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const updatePurchasedItem = async (articleId, updateData) => {
    try {
      await axios.patch(`/api/users/me/purchased/${articleId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error('Error updating purchased item:', err);
      setError('Unable to update purchase settings.');
    }
  };

  const handleLogout = () => {
    if (token) axios.post('/api/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    clearSession();
    navigate('/');
  };

  const goToCompanyHub = (companyId) => {
    const company = companyMap[companyId];
    if (company?.name) navigate(`/${encodeURIComponent(company.name)}`);
  };

  const openArticle = (article) => {
    const name = companyMap[article.company_id]?.name || article.company_name;
    if (name) navigate(`/${encodeURIComponent(name)}?a=${article._id}`);
  };

  const handleToggleRead = async (articleId, isRead) => {
    const id = articleId?.toString();
    if (!id) return;
    if (isRead) {
      setReadArticles(prev => prev.filter(v => v !== id));
    } else {
      setReadArticles(prev => [...new Set([...prev, id])]);
    }
    await updatePurchasedItem(id, { read: !isRead });
  };

  const isPurchasedToday = (purchasedAt) => {
    if (!purchasedAt) return false;
    const p = new Date(purchasedAt);
    const n = new Date();
    return p.getFullYear() === n.getFullYear() && p.getMonth() === n.getMonth() && p.getDate() === n.getDate();
  };

  const handleRefund = async (articleId) => {
    setRefundMsg('');
    try {
      const res = await axios.post(`/api/users/me/refund/${articleId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { tokensRefunded, newBalance } = res.data;
      setUser(prev => ({
        ...prev,
        tokens: newBalance,
        purchased: prev.purchased.filter(p => p.articleId?.toString() !== articleId),
      }));
      setRefundMsg(`Refunded ${tokensRefunded} Article Credits. New balance: ${newBalance}.`);
    } catch (err) {
      setRefundMsg(err.response?.data?.error || 'Refund failed.');
    } finally {
      setRefundConfirm(null);
    }
  };

  const handleArchive = async (articleId) => {
    setArchivedIds(prev => new Set([...prev, articleId]));
    setArchiveConfirm(null);
    setArticleGearOpen(null);
    await updatePurchasedItem(articleId, { archived: true });
  };

  const handleUnarchive = async (articleId) => {
    setArchivedIds(prev => { const n = new Set(prev); n.delete(articleId); return n; });
    await updatePurchasedItem(articleId, { archived: false });
  };

  const handleRemoveFromFeed = async (articleId) => {
    setHiddenFromFeedIds(prev => new Set([...prev, articleId]));
    setRemoveConfirm(null);
    setArticleGearOpen(null);
    await updatePurchasedItem(articleId, { hiddenFromFeed: true });
  };

  const handleReadExternal = async (article) => {
    try {
      const resp = await axios.get(`/api/embed/read-url/${article._id}`, { headers: { Authorization: `Bearer ${token}` } });
      window.open(resp.data.url, '_blank', 'noopener,noreferrer');
    } catch {
      window.open(article.external_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleReportBrokenLink = async (articleId) => {
    try {
      await axios.post(`/api/articles/${articleId}/report-broken-link`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setBrokenLinkReported(prev => new Set([...prev, String(articleId)]));
      setArticleGearOpen(null);
    } catch (_) {
      setBrokenLinkReported(prev => new Set([...prev, String(articleId)])); // still close dropdown
      setArticleGearOpen(null);
    }
  };

  const handleFollowCompany = async (companyId) => {
    const already = following.includes(String(companyId));
    try {
      if (already) {
        await axios.delete(`/api/users/me/follow/company/${companyId}`, { headers: { Authorization: `Bearer ${token}` } });
        setFollowing(prev => prev.filter(id => id !== String(companyId)));
        setFollowingNames(prev => ({ ...prev, companies: prev.companies.filter(c => String(c._id) !== String(companyId)) }));
      } else {
        await axios.post(`/api/users/me/follow/company/${companyId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setFollowing(prev => [...prev, String(companyId)]);
        const comp = companyMap[companyId];
        if (comp) setFollowingNames(prev => ({ ...prev, companies: [...prev.companies, { _id: companyId, company_name: comp.name }] }));
      }
    } catch (err) { console.error('Follow company error:', err); }
  };

  const handleFollowAuthor = async (authorId) => {
    const already = followingAuthors.includes(String(authorId));
    try {
      if (already) {
        await axios.delete(`/api/users/me/follow/author/${authorId}`, { headers: { Authorization: `Bearer ${token}` } });
        setFollowingAuthors(prev => prev.filter(id => id !== String(authorId)));
        setFollowingNames(prev => ({ ...prev, authors: prev.authors.filter(a => String(a._id) !== String(authorId)) }));
      } else {
        await axios.post(`/api/users/me/follow/author/${authorId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setFollowingAuthors(prev => [...prev, String(authorId)]);
      }
    } catch (err) { console.error('Follow author error:', err); }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: T.white }}>
        <nav style={{
          background: T.header,
          display: 'grid',
          gridTemplateColumns: '220px 1fr 300px',
          gridTemplateRows: '110px 36px',
          position: 'sticky', top: 0, zIndex: 1000,
          boxShadow: '0 2px 12px rgba(0,0,0,0.6)',
        }}>
          <div style={{ gridColumn: 1, gridRow: '1 / 3', background: '#F41238', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '210px', objectFit: 'contain' }} />
          </div>
          <div style={{ gridColumn: 2, gridRow: 1, background: '#13273F' }} />
          <div style={{ gridColumn: 3, gridRow: '1 / 3', background: '#13273F' }} />
          <div style={{ gridColumn: 2, gridRow: 2, background: '#0d1f33' }} />
        </nav>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', fontFamily: 'Arial, sans-serif', color: T.meta }}>
          Loading your silo…
        </div>
      </div>
    );
  }

  const purchasedArticles = user?.purchased?.filter(item => !item.returned && !hiddenFromFeedIds.has(item.articleId?.toString?.())).map(item => ({
    ...item,
    articleId: item.articleId?.toString?.(),
    folder: item.folder || 'all',
    read: item.read || false,
  })) || [];

  const allPurchasedArticles = purchasedArticles
    .map(purchase => {
      const article = articles.find(item => item._id?.toString?.() === purchase.articleId);
      return article
        ? { ...article, purchaseFolder: purchase.folder, purchaseRead: purchase.read, purchasedAt: purchase.purchasedAt, isArchived: purchase.archived || false, promoTokensUsed: purchase.promoTokensUsed || 0, purchasedTokenCost: purchase.purchasedTokenCost || 0 }
        : null;
    })
    .filter(Boolean);

  const availableGenres = [...new Set(
    allPurchasedArticles.filter(a => !archivedIds.has(a._id?.toString())).map(a => a.genre_tree?.main).filter(Boolean)
  )].sort();

  const archivedArticles = allPurchasedArticles.filter(a => archivedIds.has(a._id?.toString()));
  const archiveGenres = [...new Set(archivedArticles.map(a => a.genre_tree?.main).filter(Boolean))].sort();
  const isViewingArchive = selectedArchiveGenre !== null;

  const purchasedCompanies = [...new Set(allPurchasedArticles.map(a => String(a.company_id)))]
    .map(id => companyMap[id] ? { id, name: companyMap[id].name } : null)
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));

  const displayArticles = purchasedArticles
    .map(purchase => {
      const article = articles.find(item => item._id?.toString?.() === purchase.articleId);
      return article
        ? { ...article, purchaseFolder: purchase.folder, purchaseRead: purchase.read, purchasedAt: purchase.purchasedAt, isArchived: purchase.archived || false, promoTokensUsed: purchase.promoTokensUsed || 0, purchasedTokenCost: purchase.purchasedTokenCost || 0 }
        : null;
    })
    .filter(item => item !== null)
    .filter(article => {
      if (companyFilter) return String(article.company_id) === companyFilter;
      const aid = article._id?.toString();
      if (isViewingArchive) {
        if (!archivedIds.has(aid)) return false;
        if (selectedArchiveGenre !== 'all' && article.genre_tree?.main !== selectedArchiveGenre) return false;
        return true;
      }
      return !archivedIds.has(aid);
    })
    .filter(article => {
      if (companyFilter) return true;
      if (!isViewingArchive && selectedFollow !== 'all') {
        if (String(article.company_id) === String(selectedFollow)) return true;
        if (article.writer_id && String(article.writer_id) === String(selectedFollow)) return true;
        return false;
      }
      return true;
    })
    .filter(article => {
      if (companyFilter) return true;
      if (!isViewingArchive && genreFilter !== 'all' && article.genre_tree?.main !== genreFilter) return false;
      return true;
    })
    .filter(article => {
      if (companyFilter) return true;
      if (!isViewingArchive) {
        if (readFilter === 'read') return article.purchaseRead;
        if (readFilter === 'unread') return !article.purchaseRead;
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date_purchased_asc':  return new Date(a.purchasedAt) - new Date(b.purchasedAt);
        case 'date_purchased_desc': return new Date(b.purchasedAt) - new Date(a.purchasedAt);
        case 'date_published_asc':  return new Date(a.date_created) - new Date(b.date_created);
        case 'date_published_desc': return new Date(b.date_created) - new Date(a.date_created);
        case 'title_asc':           return a.title.localeCompare(b.title);
        case 'title_desc':          return b.title.localeCompare(a.title);
        case 'unread_first':        return (a.purchaseRead ? 1 : 0) - (b.purchaseRead ? 1 : 0);
        case 'read_first':          return (b.purchaseRead ? 1 : 0) - (a.purchaseRead ? 1 : 0);
        default: return 0;
      }
    });

  const totalFollowing = followingNames.companies.length + followingNames.authors.length;
  const collapsible = totalFollowing > 5;
  const listVisible = !collapsible || followListOpen;

  return (
    <>
    {!isMobile && <div style={{ minHeight: '100vh', background: T.white, fontFamily: "'Georgia', serif", color: T.bodyText }}>

      {/* ── Navbar ── */}
      <nav style={{
        background: T.header,
        display: 'grid',
        gridTemplateColumns: '220px 1fr 300px',
        gridTemplateRows: '110px 36px',
        position: 'sticky', top: 0, zIndex: 1000,
        boxShadow: '0 2px 12px rgba(0,0,0,0.6)',
      }}>
        {/* Logo — spans both rows */}
        <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238' }}>
          <img src={orangeLogo} alt="PubSilo"
            style={{ height: '90%', maxWidth: '210px', objectFit: 'contain', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          />
        </div>

        {/* Center: date + live time */}
        <div style={{ gridColumn: 2, gridRow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#13273F' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#aaa', fontSize: '13px', fontFamily: 'Arial, sans-serif', letterSpacing: '0.03em', marginBottom: '4px' }}>
              {now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            <div style={{ color: T.white, fontSize: '44px', fontWeight: '300', fontFamily: "'Arial', sans-serif", letterSpacing: '0.08em', lineHeight: 1 }}>
              {now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>

        {/* Right: token balance + silo label — spans both rows */}
        <div style={{ gridColumn: 3, gridRow: '1 / 3', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', background: '#13273F' }}>
          <div ref={tokenDropRef} style={{ position: 'relative' }}>
            <div
              onClick={() => setTokenDropOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', background: '#1e1e1e', border: '1px solid #333', borderRadius: '6px', padding: '7px 13px', color: T.white, fontSize: '13px', fontFamily: 'Arial, sans-serif', userSelect: 'none' }}
            >
              <span>🪙</span>
              <span style={{ fontWeight: '700' }}>{user?.tokens || 0}</span>
              <span style={{ fontSize: '9px', color: '#777', marginLeft: '2px' }}>▼</span>
            </div>
            {tokenDropOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: '8px', padding: '10px 0', minWidth: '160px', zIndex: 1001, boxShadow: '0 6px 20px rgba(0,0,0,0.5)' }}>
                <div style={{ padding: '4px 14px 8px', borderBottom: '1px solid #2a2a2a', marginBottom: '4px' }}>
                  <div style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'Arial, sans-serif' }}>Balance</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: T.white, fontFamily: 'Arial, sans-serif' }}>{user?.tokens || 0} credits</div>
                </div>
                <div
                  onClick={() => { setTokenDropOpen(false); navigate('/tokens/purchase'); }}
                  style={{ padding: '8px 14px', fontSize: '13px', color: T.accent, cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontWeight: '700' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#262626'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  + Buy More Article Credits
                </div>
              </div>
            )}
          </div>
          <span style={{ color: T.white, fontSize: '13px', fontFamily: 'Arial, sans-serif', fontWeight: '700', whiteSpace: 'nowrap' }}>
            {user?.username}'s Silo
          </span>
        </div>

        {/* Genre links strip */}
        <div style={{ gridColumn: 2, gridRow: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#13273F', borderBottom: '2px solid #000' }}>
          {GENRE_ROUTES.map(({ label, to, color }) => (
            <Link key={label} to={to}
              style={{ color: '#aaa', textDecoration: 'none', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0 20px', height: '100%', display: 'flex', alignItems: 'center', fontFamily: 'Arial, sans-serif', transition: 'background 0.12s, color 0.12s' }}
              onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = T.white; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#aaa'; }}
            >
              {label}
            </Link>
          ))}
        </div>
      </nav>

      {/* ── Body ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>

        {/* ── Left sidebar ── */}
        <div style={{ width: '220px', flexShrink: 0, borderRight: `1px solid ${T.border}`, minHeight: 'calc(100vh - 146px)', padding: '16px 0', display: 'flex', flexDirection: 'column' }}>

          {/* Following section header */}
          <div
            style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: '#888', padding: '0 12px 10px', fontFamily: 'Arial, sans-serif', borderBottom: `2px solid ${T.accent}`, marginBottom: '8px', marginLeft: '10px', marginRight: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: collapsible ? 'pointer' : 'default' }}
            onClick={collapsible ? () => setFollowListOpen(o => !o) : undefined}
          >
            <span>Following {collapsible && <span style={{ fontSize: '9px', color: '#bbb', fontWeight: '400' }}>({totalFollowing})</span>}</span>
            {collapsible && <span style={{ fontSize: '9px', color: '#aaa' }}>{followListOpen ? '▲' : '▼'}</span>}
          </div>

          {listVisible && (
            <div style={{ marginBottom: '4px' }}>
              {followingNames.companies.length > 0 && (
                <div style={{ fontSize: '10px', color: '#aaa', padding: '2px 12px 4px', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'Arial, sans-serif' }}>Companies</div>
              )}
              {followingNames.companies.map(c => (
                <div
                  key={c._id}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px 6px 12px', cursor: 'pointer', fontSize: '12px', fontFamily: 'Arial, sans-serif', position: 'relative', background: selectedFollow === String(c._id) ? '#fff5f5' : 'transparent', borderLeft: selectedFollow === String(c._id) ? `3px solid ${T.accent}` : '3px solid transparent' }}
                  onClick={() => { setSelectedFollow(String(c._id)); setGearMenuOpen(null); setSelectedArchiveGenre(null); setArchiveSectionOpen(false); }}
                  onMouseEnter={e => { if (selectedFollow !== String(c._id)) e.currentTarget.style.background = '#f9f9f9'; }}
                  onMouseLeave={e => { if (selectedFollow !== String(c._id)) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span
                    onClick={e => { e.stopPropagation(); navigate(`/${encodeURIComponent(c.company_name)}`); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', flex: 1, overflow: 'hidden', cursor: 'pointer' }}
                  >
                    <span style={{ fontSize: '12px' }}>🏢</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: T.headText, fontWeight: '600' }}>{c.company_name}</span>
                  </span>
                  <span
                    onClick={e => { e.stopPropagation(); setGearMenuOpen(gearMenuOpen === c._id ? null : c._id); }}
                    style={{ fontSize: '0.8rem', color: '#bbb', cursor: 'pointer', padding: '0 2px', flexShrink: 0 }}
                  >⚙️</span>
                  {gearMenuOpen === c._id && (
                    <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 100, background: '#fff', border: '1px solid #ddd', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '130px' }}>
                      <div
                        onClick={e => { e.stopPropagation(); handleFollowCompany(c._id); if (selectedFollow === String(c._id)) setSelectedFollow('all'); setGearMenuOpen(null); }}
                        style={{ padding: '0.5rem 0.85rem', fontSize: '0.82rem', cursor: 'pointer', color: '#dc3545' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fff5f5'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >Unfollow</div>
                    </div>
                  )}
                </div>
              ))}

              {followingNames.authors.length > 0 && (
                <div style={{ fontSize: '10px', color: '#aaa', padding: '6px 12px 4px', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'Arial, sans-serif' }}>Authors</div>
              )}
              {followingNames.authors.map(a => (
                <div
                  key={a._id}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px 6px 12px', cursor: 'pointer', fontSize: '12px', fontFamily: 'Arial, sans-serif', position: 'relative', background: selectedFollow === String(a._id) ? '#fff5f5' : 'transparent', borderLeft: selectedFollow === String(a._id) ? `3px solid ${T.accent}` : '3px solid transparent' }}
                  onClick={() => { setSelectedFollow(String(a._id)); setGearMenuOpen(null); setSelectedArchiveGenre(null); setArchiveSectionOpen(false); }}
                  onMouseEnter={e => { if (selectedFollow !== String(a._id)) e.currentTarget.style.background = '#f9f9f9'; }}
                  onMouseLeave={e => { if (selectedFollow !== String(a._id)) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span
                    onClick={e => { e.stopPropagation(); if (a.company_name) navigate(`/${encodeURIComponent(a.company_name)}`); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', flex: 1, overflow: 'hidden', cursor: 'pointer' }}
                  >
                    <span style={{ fontSize: '12px' }}>✍️</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: T.headText, fontWeight: '600' }}>{a.display_name || a.username}</span>
                  </span>
                  <span
                    onClick={e => { e.stopPropagation(); setGearMenuOpen(gearMenuOpen === a._id ? null : a._id); }}
                    style={{ fontSize: '0.8rem', color: '#bbb', cursor: 'pointer', padding: '0 2px', flexShrink: 0 }}
                  >⚙️</span>
                  {gearMenuOpen === a._id && (
                    <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 100, background: '#fff', border: '1px solid #ddd', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '130px' }}>
                      <div
                        onClick={e => { e.stopPropagation(); handleFollowAuthor(a._id); if (selectedFollow === String(a._id)) setSelectedFollow('all'); setGearMenuOpen(null); }}
                        style={{ padding: '0.5rem 0.85rem', fontSize: '0.82rem', cursor: 'pointer', color: '#dc3545' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fff5f5'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >Unfollow</div>
                    </div>
                  )}
                </div>
              ))}

              {followingNames.companies.length === 0 && followingNames.authors.length === 0 && (
                <div style={{ fontSize: '11px', color: '#bbb', padding: '4px 12px', fontFamily: 'Arial, sans-serif', lineHeight: 1.5 }}>
                  No followed sources yet. Follow companies and authors from article cards.
                </div>
              )}

            </div>
          )}

          {/* My Interests */}
          {userInterests.length > 0 && (
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: '12px', marginTop: '4px' }}>
              <div
                onClick={() => setInterestsExpanded(e => !e)}
                style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: '#888', padding: '0 12px 10px', fontFamily: 'Arial, sans-serif', borderBottom: '2px solid #8e44ad', marginBottom: '8px', marginLeft: '10px', marginRight: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              >
                <span>🎯 My Interests</span>
                <span style={{ fontSize: '9px' }}>{interestsExpanded ? '▲' : '▼'} {userInterests.length}</span>
              </div>
              {interestsExpanded && (
                <div>
                  {(userInterests.length > 5 ? userInterests.slice(0, 5) : userInterests).map((interest, i) => (
                    <div
                      key={i}
                      title={interest}
                      style={{ padding: '4px 12px', fontSize: '11px', color: T.meta, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.5, fontFamily: 'Arial, sans-serif' }}
                    >
                      {interest.split(' › ').slice(-2).join(' › ')}
                    </div>
                  ))}
                  {userInterests.length > 5 && (
                    <div style={{ padding: '2px 12px', fontSize: '10px', color: '#aaa', fontFamily: 'Arial, sans-serif' }}>
                      +{userInterests.length - 5} more
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Archived Articles */}
          {archivedIds.size > 0 && (
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: '12px', marginTop: '4px' }}>
              <div
                onClick={() => {
                  const opening = !archiveSectionOpen;
                  setArchiveSectionOpen(opening);
                  if (opening) {
                    setSelectedArchiveGenre('all');
                    setSelectedFollow('all');
                    setCompanyFilter(null);
                  } else {
                    setSelectedArchiveGenre(null);
                  }
                }}
                style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: '#888', padding: '0 12px 10px', fontFamily: 'Arial, sans-serif', borderBottom: '2px solid #555', marginBottom: '8px', marginLeft: '10px', marginRight: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              >
                <span>🗄️ Archive</span>
                <span style={{ fontSize: '9px' }}>{archiveSectionOpen ? '▲' : '▼'} {archivedIds.size}</span>
              </div>
              {archiveSectionOpen && (
                <div>
                  <div
                    onClick={() => setSelectedArchiveGenre('all')}
                    style={{ padding: '5px 12px', fontSize: '12px', fontFamily: 'Arial, sans-serif', cursor: 'pointer', color: selectedArchiveGenre === 'all' ? T.accent : T.headText, fontWeight: selectedArchiveGenre === 'all' ? '700' : '400', background: selectedArchiveGenre === 'all' ? '#fff5f5' : 'transparent', borderLeft: selectedArchiveGenre === 'all' ? `3px solid ${T.accent}` : '3px solid transparent' }}
                  >
                    📂 All Archived
                  </div>
                  {archiveGenres.map(genre => (
                    <div
                      key={genre}
                      onClick={() => setSelectedArchiveGenre(genre)}
                      style={{ padding: '5px 12px', fontSize: '12px', fontFamily: 'Arial, sans-serif', cursor: 'pointer', color: selectedArchiveGenre === genre ? T.accent : T.headText, fontWeight: selectedArchiveGenre === genre ? '700' : '400', background: selectedArchiveGenre === genre ? '#fff5f5' : 'transparent', borderLeft: selectedArchiveGenre === genre ? `3px solid ${T.accent}` : '3px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <span>📁 {genre}</span>
                      <span style={{ fontSize: '10px', color: T.meta }}>{archivedArticles.filter(a => a.genre_tree?.main === genre).length}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

        {/* ── Center: article feed ── */}
        <div style={{ flex: 1, minWidth: 0, padding: '20px 20px 48px' }}>

          {/* Feed section header */}
          <div style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: T.headText, fontFamily: 'Arial, sans-serif', borderBottom: `2px solid ${T.accent}`, paddingBottom: '7px', marginBottom: '16px' }}>
            {companyFilter
              ? `${purchasedCompanies.find(c => c.id === companyFilter)?.name || 'Company'} · All Articles`
              : isViewingArchive
                ? selectedArchiveGenre === 'all' ? 'All Archived Articles' : `Archive · ${selectedArchiveGenre}`
                : selectedFollow === 'all'
                  ? `${user?.username}'s Reading List`
                  : (() => {
                      const comp = followingNames.companies.find(c => String(c._id) === selectedFollow);
                      if (comp) return `From: ${comp.company_name}`;
                      const auth = followingNames.authors.find(a => String(a._id) === selectedFollow);
                      if (auth) return `By: ${auth.display_name || auth.username}`;
                      return `${user?.username}'s Reading List`;
                    })()
            }
          </div>

          {error && (
            <div style={{ color: 'red', marginBottom: '1rem', fontFamily: 'Arial, sans-serif', fontSize: '14px' }}>{error}</div>
          )}
          {refundMsg && (
            <div style={{ backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '6px', padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.875rem', color: '#155724', fontFamily: 'Arial, sans-serif' }}>
              {refundMsg}
            </div>
          )}

          {/* Genre filter pills */}
          {!isViewingArchive && !companyFilter && availableGenres.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.85rem' }}>
              {['all', ...availableGenres].map(g => (
                <button
                  key={g}
                  onClick={() => setGenreFilter(g)}
                  style={{
                    padding: '4px 12px', borderRadius: '3px', border: 'none',
                    fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.07em',
                    cursor: 'pointer', fontFamily: 'Arial, sans-serif',
                    background: genreFilter === g ? (GENRE_COLOR[g] || T.headText) : '#eee',
                    color: genreFilter === g ? T.white : '#555',
                    transition: 'all 0.15s',
                  }}
                >
                  {g === 'all' ? 'All' : g}
                </button>
              ))}
            </div>
          )}

          {/* Sort + filter controls */}
          {!isViewingArchive && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap', fontFamily: 'Arial, sans-serif' }}>
              {purchasedCompanies.length > 0 && (
                <select
                  value={companyFilter || ''}
                  onChange={e => { setCompanyFilter(e.target.value || null); setSelectedArchiveGenre(null); setArchiveSectionOpen(false); }}
                  style={{ padding: '0.4rem 0.75rem', borderRadius: '4px', border: `1px solid ${companyFilter ? T.accent : '#ccc'}`, fontSize: '12px', cursor: 'pointer', background: companyFilter ? '#fff5f5' : '#fff', fontWeight: companyFilter ? '700' : '400', fontFamily: 'Arial, sans-serif' }}
                >
                  <option value="">All Companies</option>
                  {purchasedCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                style={{ padding: '0.4rem 0.75rem', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px', cursor: 'pointer', fontFamily: 'Arial, sans-serif' }}
              >
                <option value="date_purchased_desc">Purchased: Newest</option>
                <option value="date_purchased_asc">Purchased: Oldest</option>
                <option value="date_published_desc">Published: Newest</option>
                <option value="date_published_asc">Published: Oldest</option>
                <option value="title_asc">Title: A → Z</option>
                <option value="title_desc">Title: Z → A</option>
                <option value="unread_first">Unread First</option>
                <option value="read_first">Read First</option>
              </select>
              <div style={{ display: 'flex', gap: '4px' }}>
                {['all', 'unread', 'read'].map(f => (
                  <button
                    key={f}
                    onClick={() => setReadFilter(f)}
                    style={{ padding: '0.35rem 0.85rem', borderRadius: '4px', border: 'none', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', fontFamily: 'Arial, sans-serif', background: readFilter === f ? T.headText : '#eee', color: readFilter === f ? T.white : '#555' }}
                  >
                    {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
              <span style={{ marginLeft: 'auto', fontSize: '12px', color: T.meta }}>
                {displayArticles.length} article{displayArticles.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* Article list */}
          {displayArticles.length === 0 ? (
            <div style={{ textAlign: 'center', color: T.meta, padding: '3rem', fontFamily: 'Arial, sans-serif' }}>
              No articles in this collection yet. Start purchasing articles!
            </div>
          ) : (
            <div className="article-feed">
              {displayArticles.map(article => {
                const articleId = article._id?.toString();
                const isRead = readArticles.includes(articleId);
                const comp = companyMap[article.company_id] || {};
                const cardStyle = article.cardStyle || comp.cardStyle || '6';
                const isFollowingComp = following.includes(String(article.company_id));
                const isFollowingAuth = article.writer_id && followingAuthors.includes(String(article.writer_id));
                const blurbText = article.excerpt || (article.content ? article.content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : '').substring(0, 200);
                const dateStr = new Date(article.date_created).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

                const sqSty  = { display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', border: '1px solid #d4d1cb', borderRadius: '5px', backgroundColor: '#f0ede7', textDecoration: 'none', gap: '1px', flexShrink: 0, cursor: 'pointer' };
                const sqStyC = { ...sqSty, border: '1px solid #c8c4bb', backgroundColor: '#f0ede7' };

                const gearEl = (
                  <div style={{ position: 'absolute', top: '0.4rem', right: '0.4rem', zIndex: 10 }} onClick={e => e.stopPropagation()}>
                    <div style={{ position: 'relative' }}>
                      <span onClick={() => { setArticleGearOpen(articleGearOpen === articleId ? null : articleId); setArchiveConfirm(null); setRemoveConfirm(null); }}
                        style={{ fontSize: '0.78rem', color: '#999', cursor: 'pointer', padding: '0.15rem 0.3rem', borderRadius: '4px', userSelect: 'none', lineHeight: 1 }} title="Options">⚙️</span>
                      {articleGearOpen === articleId && (
                        <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 200, background: '#fff', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: '170px', padding: '0.25rem 0' }}>
                          {archiveConfirm === articleId ? (
                            <div style={{ padding: '0.5rem 0.85rem' }}>
                              <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.4rem' }}>Archive this article?</div>
                              <div style={{ display: 'flex', gap: '0.4rem' }}>
                                <button onClick={() => handleArchive(articleId)} style={{ flex: 1, padding: '0.25rem 0', backgroundColor: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.78rem', cursor: 'pointer' }}>Yes, Archive</button>
                                <button onClick={() => setArchiveConfirm(null)} style={{ flex: 1, padding: '0.25rem 0', backgroundColor: '#f0f0f0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.78rem', cursor: 'pointer' }}>Cancel</button>
                              </div>
                            </div>
                          ) : removeConfirm === articleId ? (
                            <div style={{ padding: '0.5rem 0.85rem' }}>
                              <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.2rem' }}>Remove from your feed?</div>
                              <div style={{ fontSize: '0.73rem', color: '#999', marginBottom: '0.4rem' }}>Purchase history is kept.</div>
                              <div style={{ display: 'flex', gap: '0.4rem' }}>
                                <button onClick={() => handleRemoveFromFeed(articleId)} style={{ flex: 1, padding: '0.25rem 0', backgroundColor: '#cc3333', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.78rem', cursor: 'pointer' }}>Remove</button>
                                <button onClick={() => setRemoveConfirm(null)} style={{ flex: 1, padding: '0.25rem 0', backgroundColor: '#f0f0f0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.78rem', cursor: 'pointer' }}>Cancel</button>
                              </div>
                            </div>
                          ) : article.isArchived ? (
                            <div onClick={() => handleUnarchive(articleId)} style={{ padding: '0.5rem 0.85rem', fontSize: '0.82rem', cursor: 'pointer', color: '#007bff' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#f0f7ff'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>↩ Unarchive</div>
                          ) : (
                            <>
                              <div onClick={() => { setArchiveConfirm(articleId); setRemoveConfirm(null); }} style={{ padding: '0.5rem 0.85rem', fontSize: '0.82rem', cursor: 'pointer', color: '#555' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>🗄️ Archive</div>
                              <div onClick={() => { setRemoveConfirm(articleId); setArchiveConfirm(null); }} style={{ padding: '0.5rem 0.85rem', fontSize: '0.82rem', cursor: 'pointer', color: '#cc3333', borderTop: '1px solid #f5f5f5' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#fff5f5'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>✕ Remove from Feed</div>
                            </>
                          )}
                          {article.external_url && !brokenLinkReported.has(String(articleId)) && (
                            <div
                              onClick={() => handleReportBrokenLink(articleId)}
                              style={{ padding: '0.5rem 0.85rem', fontSize: '0.82rem', cursor: 'pointer', color: '#cc0000', borderTop: '1px solid #f0f0f0' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#fff5f5'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >🔗 Report Broken Link</div>
                          )}
                          {article.external_url && brokenLinkReported.has(String(articleId)) && (
                            <div style={{ padding: '0.5rem 0.85rem', fontSize: '0.78rem', color: '#aaa', borderTop: '1px solid #f0f0f0' }}>✓ Link reported</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );

                const readToggleEl = (
                  <div onClick={() => handleToggleRead(articleId, isRead)} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', flexShrink: 0 }} title={isRead ? 'Mark as unread' : 'Mark as read'}>
                    <span style={{ fontSize: '0.6rem', color: isRead ? '#28a745' : '#bbb', fontWeight: '600' }}>{isRead ? 'Read' : 'Unread'}</span>
                    <div style={{ width: '28px', height: '15px', borderRadius: '8px', position: 'relative', flexShrink: 0, backgroundColor: isRead ? '#28a745' : '#ccc', transition: 'background-color 0.2s' }}>
                      <div style={{ position: 'absolute', top: '1px', left: isRead ? '14px' : '1px', width: '13px', height: '13px', borderRadius: '50%', backgroundColor: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    </div>
                  </div>
                );

                const readBtnSty = { padding: '0.38rem 1rem', fontSize: '0.82rem', fontWeight: '700', border: 'none', borderRadius: '6px', backgroundColor: '#1a1a1a', color: '#fff', fontFamily: 'Georgia, serif', flexShrink: 0, cursor: 'pointer', textDecoration: 'none', display: 'inline-block' };
                const readBtnEl = article.external_url ? (
                  <button onClick={() => handleReadExternal(article)} style={readBtnSty}>Read Article ↗</button>
                ) : (
                  <button onClick={() => openArticle(article)} style={readBtnSty}>Read Article</button>
                );

                const makeImgEl = (r) => article.headline_image ? (
                  <img src={article.headline_image} alt="" style={{ width: '100%', height: 'auto', maxHeight: '200px', objectFit: 'contain', display: 'block', borderRadius: r }} />
                ) : comp.heroImage ? (
                  <img src={comp.heroImage} alt="" style={{ width: '100%', height: 'auto', maxHeight: '200px', objectFit: 'contain', display: 'block', borderRadius: r }} />
                ) : (
                  <div style={{ width: '100%', height: '80px', backgroundColor: '#1a1a1a', borderRadius: r, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: '#fff', fontSize: '0.58rem', fontWeight: '700', textAlign: 'center', padding: '0 4px' }}>{(comp.name || '').substring(0, 12)}</span>
                  </div>
                );

                const costBadge = article.token_cost > 0 && (
                  <span style={{ fontSize: '0.78rem', fontWeight: '700', color: '#7b2ff7', backgroundColor: '#f3eeff', padding: '0.2rem 0.6rem', borderRadius: '12px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {article.token_cost} 🪙
                  </span>
                );

                const engagementCluster = (sty) => (
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {!isViewingArchive && !isFollowingComp && (
                      <button type="button" onClick={() => handleFollowCompany(article.company_id)} title="Follow Publisher" style={{ ...sty, background: sty.backgroundColor, border: sty.border, padding: 0 }}>
                        {[<span key="s" style={{ fontSize: '0.72rem', fontWeight: '700', color: '#555', lineHeight: 1 }}>+</span>, <span key="a" style={{ fontSize: '0.44rem', color: '#888', lineHeight: 1.15 }}>Follow</span>, <span key="b" style={{ fontSize: '0.44rem', color: '#888', lineHeight: 1.15 }}>Publisher</span>]}
                      </button>
                    )}
                    {!isViewingArchive && article.writer_id && !isFollowingAuth && (
                      <button type="button" onClick={() => handleFollowAuthor(article.writer_id)} title="Follow Author" style={{ ...sty, background: sty.backgroundColor, border: sty.border, padding: 0 }}>
                        {[<span key="s" style={{ fontSize: '0.72rem', fontWeight: '700', color: '#555', lineHeight: 1 }}>+</span>, <span key="a" style={{ fontSize: '0.44rem', color: '#888', lineHeight: 1.15 }}>Follow</span>, <span key="b" style={{ fontSize: '0.44rem', color: '#888', lineHeight: 1.15 }}>Author</span>]}
                      </button>
                    )}
                    {!isViewingArchive && (
                      <button type="button" onClick={() => goToCompanyHub(article.company_id)} title="Company Silo" style={{ ...sty, background: sty.backgroundColor, border: sty.border, padding: 0 }}>
                        {[<span key="s" style={{ fontSize: '0.72rem', fontWeight: '700', color: '#555', lineHeight: 1 }}>↗</span>, <span key="a" style={{ fontSize: '0.44rem', color: '#888', lineHeight: 1.15 }}>Company</span>, <span key="b" style={{ fontSize: '0.44rem', color: '#888', lineHeight: 1.15 }}>Silo</span>]}
                      </button>
                    )}
                    {readToggleEl}
                  </div>
                );

                const refundEl = returnsEnabled && returnsThisMonth < 2 && article.promoTokensUsed < article.purchasedTokenCost && (
                  refundConfirm === articleId ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                      <span style={{ fontSize: '0.73rem', color: '#555' }}>{article.token_cost}🪙?</span>
                      <button onClick={() => handleRefund(articleId)} style={{ padding: '0.18rem 0.45rem', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.72rem', cursor: 'pointer' }}>Yes</button>
                      <button onClick={() => setRefundConfirm(null)} style={{ padding: '0.18rem 0.45rem', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.72rem', cursor: 'pointer' }}>No</button>
                    </span>
                  ) : (
                    <button onClick={() => { setRefundMsg(''); setRefundConfirm(articleId); }} style={{ padding: '0.2rem 0.6rem', backgroundColor: 'transparent', color: '#888', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer', flexShrink: 0 }}>Return</button>
                  )
                );

                return cardStyle === 'C' ? (
                  <div key={article._id} style={{ position: 'relative', marginBottom: '1.25rem', background: '#fafaf8', border: `1px solid ${isRead ? '#b2d8b2' : '#c8c4bb'}`, borderRadius: '6px', overflow: 'hidden', opacity: isRead ? 0.85 : 1 }}>
                    {gearEl}
                    <div style={{ borderBottom: '2px solid #1a1a1a', padding: '0.75rem 2.8rem 0.55rem 1.1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{ fontFamily: 'Georgia, serif', fontSize: '1rem', fontWeight: '700', letterSpacing: '0.05em', color: '#1a1a1a' }}>{comp.name || 'Publisher'}</span>
                      <span style={{ fontSize: '0.65rem', color: '#888' }}>{dateStr}</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <div style={{ flex: 1, padding: '0 1.1rem', borderRight: '1px solid #c8c4bb' }}>
                        <div style={{ paddingTop: '0.9rem', paddingBottom: '0.9rem' }}>
                          {makeImgEl('3px')}
                          <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '0.95rem', fontWeight: '700', lineHeight: 1.3, margin: '0.6rem 0 0.4rem' }}>{article.title}</h3>
                          {article.wasEdited && article.editedAt && (
                            <div style={{ fontSize: '0.62rem', color: '#aaa', fontStyle: 'italic', marginTop: '0.15rem' }}>
                              *Edited {new Date(article.editedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                          )}
                          {article.genre_tree?.sub && <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#888', fontWeight: '700' }}>{article.genre_tree.sub}</div>}
                        </div>
                      </div>
                      <div style={{ flex: 1, padding: '0 1.1rem' }}>
                        <div style={{ paddingTop: '0.9rem', paddingBottom: '0.9rem' }}>
                          <p style={{ fontFamily: 'Georgia, serif', fontSize: '0.82rem', color: '#444', lineHeight: 1.7, margin: '0 0 0.75rem' }}>{blurbText}...</p>
                          {article.writer_name?.[0] && <div style={{ fontFamily: 'Georgia, serif', fontSize: '0.72rem', color: '#777', fontStyle: 'italic' }}>By {article.writer_name[0]}</div>}
                        </div>
                      </div>
                    </div>
                    <div style={{ borderTop: '1px solid #c8c4bb', backgroundColor: '#f0ede7', padding: '0.65rem 1.1rem', display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      {readBtnEl}
                      {costBadge}
                      {isViewingArchive && <button onClick={() => handleUnarchive(articleId)} style={{ padding: '0.2rem 0.6rem', backgroundColor: 'transparent', color: '#007bff', border: '1px solid #007bff', borderRadius: '4px', fontSize: '0.76rem', cursor: 'pointer', flexShrink: 0 }}>↩ Unarchive</button>}
                      {refundEl}
                      <ShareButton title={article.title} url={`/${encodeURIComponent(comp.name || '')}?a=${article._id}`} />
                      {engagementCluster(sqStyC)}
                    </div>
                  </div>
                ) : (
                  <div key={article._id} style={{ position: 'relative', marginBottom: '1.25rem', background: '#fafaf8', border: `1px solid ${isRead ? '#b2d8b2' : '#d8d5cf'}`, borderRadius: '8px', padding: '1.25rem 1.25rem 0', overflow: 'hidden', opacity: isRead ? 0.85 : 1 }}>
                    {gearEl}
                    <div style={{ borderBottom: '2px solid #1a1a1a', marginBottom: '0.75rem', paddingBottom: '0.4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingRight: '1.5rem' }}>
                      <span style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.12em', color: '#333' }}>
                        {comp.name || 'Publisher'}{article.genre_tree?.main ? ` · ${article.genre_tree.main}` : ''}
                      </span>
                      <span style={{ fontSize: '0.7rem', color: '#888' }}>{dateStr}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '1.25rem' }}>
                      <div style={{ flex: 2, minWidth: 0 }}>
                        <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '1.15rem', fontWeight: '700', lineHeight: 1.3, margin: '0 0 0.25rem' }}>{article.title}</h3>
                        {article.wasEdited && article.editedAt && (
                          <div style={{ fontSize: '0.65rem', color: '#aaa', fontStyle: 'italic', marginBottom: '0.4rem' }}>
                            *Edited {new Date(article.editedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        )}
                        <p style={{ fontFamily: 'Georgia, serif', fontSize: '0.87rem', color: '#444', lineHeight: 1.7, margin: 0 }}>{blurbText}...</p>
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem', minWidth: 0 }}>
                        {makeImgEl('4px')}
                        {article.writer_name?.[0] && <div style={{ fontSize: '0.72rem', color: '#777', fontStyle: 'italic' }}>By {article.writer_name[0]}</div>}
                      </div>
                    </div>
                    <div style={{ borderTop: '1px solid #e0ddd6', backgroundColor: '#f0ede7', margin: '1rem -1.25rem 0', padding: '0.65rem 1.25rem', display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      {readBtnEl}
                      {costBadge}
                      {isViewingArchive && <button onClick={() => handleUnarchive(articleId)} style={{ padding: '0.2rem 0.6rem', backgroundColor: 'transparent', color: '#007bff', border: '1px solid #007bff', borderRadius: '4px', fontSize: '0.76rem', cursor: 'pointer', flexShrink: 0 }}>↩ Unarchive</button>}
                      {refundEl}
                      <ShareButton title={article.title} url={`/${encodeURIComponent(comp.name || '')}?a=${article._id}`} />
                      {engagementCluster(sqSty)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Right sidebar ── */}
        <div style={{ width: '300px', flexShrink: 0, borderLeft: `1px solid ${T.border}`, minHeight: 'calc(100vh - 146px)', padding: '20px 16px' }}>

          {/* For You */}
          <div style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: '#888', fontFamily: 'Arial, sans-serif', borderBottom: `2px solid ${T.accent}`, paddingBottom: '7px', marginBottom: '14px' }}>
            For You
          </div>
          {forYouHasSignal === false && (
            <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '10px', lineHeight: 1.5, fontFamily: 'Arial, sans-serif' }}>
              Purchase articles or set interests to personalize your feed.
            </div>
          )}
          {forYouArticles.length > 0
            ? forYouArticles.map(a => <SideCard key={a._id} article={a} onClick={() => openArticle(a)} />)
            : forYouHasSignal !== false && (
                <div style={{ fontSize: '12px', color: T.meta, fontFamily: 'Arial, sans-serif' }}>No recommendations yet.</div>
              )
          }

        </div>
      </div>
    </div>}

    {/* ── Desktop: Settings (fixed bottom-left) ── */}
    {!isMobile && <div style={{ position: 'fixed', bottom: 0, left: 0, width: '220px', zIndex: 1001, background: T.white, borderRight: `1px solid ${T.border}`, paddingBottom: '10px' }}>
      <SettingsPanel onLogout={handleLogout} onInterestsChange={setUserInterests} />
    </div>}

    {/* ── Mobile Layout ── */}
    {isMobile && (
      <div style={{ minHeight: '100vh', background: T.white, fontFamily: "'Georgia', serif" }}>

        {/* Mobile Navbar */}
        <nav style={{ position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
          <div style={{ display: 'flex', height: '70px' }}>
            {/* Logo — 50% */}
            <div style={{ width: '50%', background: '#F41238', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '100%', objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/')} />
            </div>
            {/* Token info — 50% */}
            <div style={{ width: '50%', background: '#13273F', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              <div style={{ color: '#aaa', fontSize: '11px', fontFamily: 'Arial, sans-serif', fontWeight: '700', letterSpacing: '0.05em' }}>My Silo</div>
              <div
                onClick={() => navigate('/tokens/purchase')}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', background: '#1e1e1e', border: '1px solid #333', borderRadius: '5px', padding: '5px 10px', color: T.white, fontSize: '12px', fontFamily: 'Arial, sans-serif' }}
              >
                <span>🪙</span>
                <span style={{ fontWeight: '700' }}>{user?.tokens || 0}</span>
                <span style={{ fontSize: '10px', color: T.accent, marginLeft: '2px', fontWeight: '700' }}>+ Buy</span>
              </div>
            </div>
          </div>

          {/* Mobile accordion nav strip */}
          <div style={{ background: '#0d1f33', display: 'flex', borderTop: '1px solid #1a2f45' }}>
            {[
              { key: 'following', label: 'Following' },
              { key: 'archive',   label: 'Archive' },
              { key: 'settings',  label: 'Settings' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setMobileSection(s => s === key ? null : key)}
                style={{ flex: 1, background: mobileSection === key ? '#13273F' : 'transparent', color: mobileSection === key ? T.white : '#aaa', border: 'none', borderBottom: mobileSection === key ? `2px solid ${T.accent}` : '2px solid transparent', padding: '8px 4px', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer', fontFamily: 'Arial, sans-serif' }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Accordion content panels */}
          {mobileSection === 'following' && (
            <div style={{ background: T.white, borderBottom: `1px solid ${T.border}`, padding: '10px 0', maxHeight: '40vh', overflowY: 'auto' }}>
              {followingNames.companies.length > 0 && (
                <div style={{ fontSize: '10px', color: '#aaa', padding: '2px 14px 6px', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'Arial, sans-serif' }}>Companies</div>
              )}
              {followingNames.companies.map(c => (
                <div key={c._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', fontFamily: 'Arial, sans-serif', borderLeft: selectedFollow === String(c._id) ? `3px solid ${T.accent}` : '3px solid transparent', background: selectedFollow === String(c._id) ? '#fff5f5' : 'transparent' }}
                  onClick={() => { setSelectedFollow(String(c._id)); setMobileSection(null); setSelectedArchiveGenre(null); setArchiveSectionOpen(false); }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, overflow: 'hidden' }}>
                    <span>🏢</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: T.headText, fontWeight: '600' }}>{c.company_name}</span>
                  </span>
                  <span onClick={e => { e.stopPropagation(); handleFollowCompany(c._id); if (selectedFollow === String(c._id)) setSelectedFollow('all'); }} style={{ fontSize: '11px', color: '#dc3545', padding: '2px 6px', cursor: 'pointer' }}>Unfollow</span>
                </div>
              ))}
              {followingNames.authors.length > 0 && (
                <div style={{ fontSize: '10px', color: '#aaa', padding: '8px 14px 4px', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'Arial, sans-serif' }}>Authors</div>
              )}
              {followingNames.authors.map(a => (
                <div key={a._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', fontFamily: 'Arial, sans-serif', borderLeft: selectedFollow === String(a._id) ? `3px solid ${T.accent}` : '3px solid transparent', background: selectedFollow === String(a._id) ? '#fff5f5' : 'transparent' }}
                  onClick={() => { setSelectedFollow(String(a._id)); setMobileSection(null); setSelectedArchiveGenre(null); setArchiveSectionOpen(false); }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, overflow: 'hidden' }}>
                    <span>✍️</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: T.headText, fontWeight: '600' }}>{a.display_name || a.username}</span>
                  </span>
                  <span onClick={e => { e.stopPropagation(); handleFollowAuthor(a._id); if (selectedFollow === String(a._id)) setSelectedFollow('all'); }} style={{ fontSize: '11px', color: '#dc3545', padding: '2px 6px', cursor: 'pointer' }}>Unfollow</span>
                </div>
              ))}
              {followingNames.companies.length === 0 && followingNames.authors.length === 0 && (
                <div style={{ fontSize: '12px', color: '#bbb', padding: '8px 14px', fontFamily: 'Arial, sans-serif' }}>No followed sources yet.</div>
              )}
              {selectedFollow !== 'all' && (
                <div style={{ padding: '6px 14px' }}>
                  <button onClick={() => setSelectedFollow('all')} style={{ fontSize: '11px', color: T.meta, background: 'transparent', border: `1px solid ${T.border}`, borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontFamily: 'Arial, sans-serif' }}>
                    ✕ Clear filter
                  </button>
                </div>
              )}
            </div>
          )}

          {mobileSection === 'archive' && (
            <div style={{ background: T.white, borderBottom: `1px solid ${T.border}`, padding: '10px 0', maxHeight: '40vh', overflowY: 'auto' }}>
              {archivedIds.size === 0 ? (
                <div style={{ fontSize: '12px', color: '#bbb', padding: '8px 14px', fontFamily: 'Arial, sans-serif' }}>No archived articles yet.</div>
              ) : (
                <>
                  <div onClick={() => { setSelectedArchiveGenre('all'); setSelectedFollow('all'); setArchiveSectionOpen(true); setMobileSection(null); }} style={{ padding: '8px 14px', fontSize: '13px', fontFamily: 'Arial, sans-serif', cursor: 'pointer', color: selectedArchiveGenre === 'all' ? T.accent : T.headText, fontWeight: selectedArchiveGenre === 'all' ? '700' : '400', background: selectedArchiveGenre === 'all' ? '#fff5f5' : 'transparent' }}>
                    📂 All Archived ({archivedIds.size})
                  </div>
                  {archiveGenres.map(genre => (
                    <div key={genre} onClick={() => { setSelectedArchiveGenre(genre); setSelectedFollow('all'); setArchiveSectionOpen(true); setMobileSection(null); }} style={{ padding: '8px 14px', fontSize: '13px', fontFamily: 'Arial, sans-serif', cursor: 'pointer', color: selectedArchiveGenre === genre ? T.accent : T.headText, background: selectedArchiveGenre === genre ? '#fff5f5' : 'transparent', display: 'flex', justifyContent: 'space-between' }}>
                      <span>📁 {genre}</span>
                      <span style={{ fontSize: '11px', color: T.meta }}>{archivedArticles.filter(a => a.genre_tree?.main === genre).length}</span>
                    </div>
                  ))}
                  {selectedArchiveGenre !== null && (
                    <div style={{ padding: '6px 14px' }}>
                      <button onClick={() => { setSelectedArchiveGenre(null); setArchiveSectionOpen(false); }} style={{ fontSize: '11px', color: T.meta, background: 'transparent', border: `1px solid ${T.border}`, borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontFamily: 'Arial, sans-serif' }}>
                        ✕ Exit archive
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {mobileSection === 'settings' && (
            <div style={{ background: T.white, borderBottom: `1px solid ${T.border}` }}>
              <SettingsPanel onLogout={handleLogout} onInterestsChange={setUserInterests} />
            </div>
          )}

          {/* Tertiary strip — sort & filter */}
          {!isViewingArchive && (
            <div style={{ background: '#0a1828', display: 'flex', alignItems: 'center', padding: '0 8px', height: '36px', gap: '6px', borderTop: '1px solid #1a2f45', overflowX: 'auto' }}>
              {purchasedCompanies.length > 0 && (
                <select
                  value={companyFilter || ''}
                  onChange={e => { setCompanyFilter(e.target.value || null); setSelectedArchiveGenre(null); setArchiveSectionOpen(false); }}
                  style={{ padding: '3px 6px', borderRadius: '3px', border: 'none', fontSize: '11px', background: companyFilter ? '#2a1a1a' : '#1e2d40', color: companyFilter ? T.accent : T.white, cursor: 'pointer', maxWidth: '110px', fontFamily: 'Arial, sans-serif' }}
                >
                  <option value="">All Co.</option>
                  {purchasedCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
              {availableGenres.length > 0 && (
                <select
                  value={genreFilter}
                  onChange={e => setGenreFilter(e.target.value)}
                  style={{ padding: '3px 6px', borderRadius: '3px', border: 'none', fontSize: '11px', background: genreFilter !== 'all' ? '#1a1a2e' : '#1e2d40', color: genreFilter !== 'all' ? '#7b8fff' : T.white, cursor: 'pointer', maxWidth: '110px', fontFamily: 'Arial, sans-serif' }}
                >
                  <option value="all">All Genres</option>
                  {availableGenres.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              )}
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                style={{ padding: '3px 6px', borderRadius: '3px', border: 'none', fontSize: '11px', background: '#1e2d40', color: T.white, cursor: 'pointer', flex: 1, minWidth: '90px', fontFamily: 'Arial, sans-serif' }}
              >
                <option value="date_purchased_desc">Newest Purch.</option>
                <option value="date_purchased_asc">Oldest Purch.</option>
                <option value="date_published_desc">Newest Pub.</option>
                <option value="date_published_asc">Oldest Pub.</option>
                <option value="title_asc">Title A→Z</option>
                <option value="title_desc">Title Z→A</option>
                <option value="unread_first">Unread First</option>
                <option value="read_first">Read First</option>
              </select>
              <select
                value={readFilter}
                onChange={e => setReadFilter(e.target.value)}
                style={{ padding: '3px 6px', borderRadius: '3px', border: 'none', fontSize: '11px', background: '#1e2d40', color: T.white, cursor: 'pointer', fontFamily: 'Arial, sans-serif' }}
              >
                <option value="all">All</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
              </select>
            </div>
          )}
        </nav>

        {/* Mobile article feed */}
        <div style={{ padding: '12px' }}>
          <div style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: T.headText, fontFamily: 'Arial, sans-serif', borderBottom: `2px solid ${T.accent}`, paddingBottom: '6px', marginBottom: '12px' }}>
            {isViewingArchive
              ? selectedArchiveGenre === 'all' ? 'All Archived Articles' : `Archive · ${selectedArchiveGenre}`
              : selectedFollow === 'all'
                ? `${user?.username}'s Reading List`
                : (() => {
                    const comp = followingNames.companies.find(c => String(c._id) === selectedFollow);
                    if (comp) return `From: ${comp.company_name}`;
                    const auth = followingNames.authors.find(a => String(a._id) === selectedFollow);
                    if (auth) return `By: ${auth.display_name || auth.username}`;
                    return `${user?.username}'s Reading List`;
                  })()
            }
          </div>
          {displayArticles.length === 0 ? (
            <div style={{ textAlign: 'center', color: T.meta, padding: '2rem', fontFamily: 'Arial, sans-serif', fontSize: '13px' }}>
              No articles in this collection yet. Start purchasing articles!
            </div>
          ) : displayArticles.map(article => {
            const articleId = article._id?.toString();
            const isRead = readArticles.includes(articleId);
            const color = GENRE_COLOR[article.genre_tree?.main] || T.accent;
            return (
              <div key={articleId} style={{ display: 'flex', gap: '10px', marginBottom: '14px', paddingBottom: '14px', borderBottom: `1px solid ${T.border}` }}>
                {article.headline_image && (
                  <div style={{ width: '90px', height: '68px', flexShrink: 0, overflow: 'hidden', borderRadius: '3px' }}>
                    <img src={article.headline_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '10px', fontWeight: '800', color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px', fontFamily: 'Arial, sans-serif' }}>{article.genre_tree?.main}</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: T.headText, lineHeight: '19px', fontFamily: 'Georgia, serif', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: '4px' }}>{article.title}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: '11px', color: T.meta, fontFamily: 'Arial, sans-serif' }}>{article.company_name}</div>
                    <div onClick={() => handleToggleRead(articleId, isRead)} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', flexShrink: 0 }}>
                      <span style={{ fontSize: '10px', color: isRead ? '#28a745' : '#bbb', fontFamily: 'Arial, sans-serif' }}>{isRead ? 'Read' : 'Unread'}</span>
                      <div style={{ width: '26px', height: '14px', borderRadius: '7px', position: 'relative', background: isRead ? '#28a745' : '#ccc', transition: 'background 0.2s', flexShrink: 0 }}>
                        <div style={{ position: 'absolute', top: '1px', left: isRead ? '13px' : '1px', width: '12px', height: '12px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )}

    </>
  );
}

export default Dashboard;
